import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  styled,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { layoutCSS } from "../component/common/commonStyle";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";

import {
  useGetDbFieldsQuery,
  useGetDownloadColumnNamesMutation,
  usePostUploadDataMutation,
  usePostuploadsizeMutation,
} from "../store/services/createDbApi";

export default function UploadDataDB() {
  // ============== States ==============
  const [selectedFile, setSelectedFile] = useState(null); // the .zip file itself
  const [selectedOption, setSelectedOption] = useState("1");
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [extractedSheetName, setExtractedSheetName] = useState(""); // sheet name from the file inside the zip
  const [postUploadData] = usePostUploadDataMutation();
  const [postuploadsize] = usePostuploadsizeMutation();
  const { data: dbResponse } = useGetDbFieldsQuery();
  const [downloadColumnNames, { isLoading: isDownloadingColumns }] =
    useGetDownloadColumnNamesMutation();
  const [uploadStatus, setUploadStatus] = useState(null);

  // Extract DB fields safely
  const dbFields = Array.isArray(dbResponse?.data)
    ? dbResponse.data.map((f) => f.trim().toLowerCase())
    : [];

  // ============== Styled Component ==============
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  // ============== Handlers ==============
  const handleOptionChange = (event) => {
    const val = event.target.value;
    setSelectedOption(val);

    if (selectedFile) {
      handleUploadSize(selectedFile, val);
    } else {
      setUploadStatus(null);
    }
  };

  const handleDownloadColumnNames = async (type) => {
    try {
      const result = await downloadColumnNames(type).unwrap();
      const blobUrl = window.URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Column names download error:", err);
      alert("Failed to download column names. Try again.");
    }
  };

  // Reads the zip, pulls out the single Excel/CSV file inside it, and
  // parses that file's headers (for the match/mismatch preview UI).
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setExcelHeaders([]);
    setExtractedSheetName("");

    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip file.");
      setSelectedFile(null);
      return;
    }

    try {
      const zip = await JSZip.loadAsync(file);

      const innerEntryName = Object.keys(zip.files).find(
        (name) => !zip.files[name].dir && /\.(xlsx|xls|csv)$/i.test(name)
      );

      if (!innerEntryName) {
        toast.error("No Excel or CSV file found inside the zip.");
        return;
      }

      const innerFileData = await zip.files[innerEntryName].async(
        "arraybuffer"
      );
      const wb = XLSX.read(innerFileData, { type: "array" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (Array.isArray(data) && data.length > 0) {
        const headers = data[0]
          .filter((h) => h !== undefined && h !== null && h !== "")
          .map((h) => String(h).trim().toLowerCase());
        setExcelHeaders(headers);
        setExtractedSheetName(wsname);
        handleUploadSize(file, selectedOption);
      }
    } catch (err) {
      console.error("Error reading zip file:", err);
      toast.error(
        "Could not read the zip file. Make sure it contains a valid Excel/CSV file."
      );
    }
  };

  // Sends the zip file (not the extracted sheet) for size validation.
const handleUploadSize = async (fileArg, operation = selectedOption) => {
  const file = fileArg instanceof File ? fileArg : selectedFile;

  if (!file) {
    toast.error("Please upload a file first.");
    return;
  }

  try {
    const result = await postuploadsize({
      operationType: Number(operation),
    }).unwrap();

    setUploadStatus(result);
  } catch (err) {
    if (err?.data) {
      setUploadStatus(err.data);
    }

    toast.error(
      err?.data?.message || err?.message || "Failed to check upload status"
    );
  }
};

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please upload a file.");
      return;
    }

    const mapping = {};
    dbFields.forEach((field) => {
      const match = excelHeaders.find((col) => col === field);
      if (match) mapping[field] = match;
    });

    const dataFields1 = Object.entries(mapping)
      .map(([dbField, excelField]) => `${dbField}=${excelField}`)
      .join(",");

    const customer_dataFields = Object.values(mapping).join(",");
    const my_DataFields = Object.keys(mapping).join(",");

    const formData = new FormData();
    formData.append("operationType", selectedOption);
    formData.append("dataFields1", dataFields1);
    formData.append("customer_dataFields", customer_dataFields);
    formData.append("my_DataFields", my_DataFields);
    formData.append("SheetName", extractedSheetName);
    formData.append("excelFile", selectedFile);

    try {
      const response = await postUploadData(formData).unwrap();
      toast.success(response?.message || "Data uploaded successfully!");
    } catch (error) {
      console.error("Upload failed:", error);
      const errorData = error?.data;
      const errorMessage =
        errorData?.message || "Upload failed. Please try again.";
      const limitInfo =
        errorData?.data?.usedMB && errorData?.data?.limitMB
          ? ` (Used: ${errorData.data.usedMB} MB /Your upload limit: ${errorData.data.limitMB} MB)`
          : "";
      toast.error(`Upload failed: ${errorMessage}${limitInfo}`);
    }
  };

  const allMatch =
    excelHeaders.length > 0 &&
    dbFields.length > 0 &&
    excelHeaders.length === dbFields.length &&
    excelHeaders.every((val, idx) => val === dbFields[idx]);

  // ============== JSX Return ==============
  return (
    <Box sx={layoutCSS}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Upload Data
      </Typography>

      {/* Upload + Download Buttons */}
      <Grid
        container
        spacing={2}
        padding={2}
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        <Grid item xs={6}>
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
            sx={{ width: "100%", height: 40, textTransform: "none" }}
          >
            Choose File {selectedFile ? ` - ${selectedFile.name}` : ""}
            <VisuallyHiddenInput
              type="file"
              id="fileUpload"
              accept=".zip"
              onChange={handleFileChange}
            />
          </Button>
          <Typography
            variant="caption"
            sx={{
              color: selectedFile ? "#2e7d32" : "#757575",
              mt: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {selectedFile ? (
              <>
                <CheckCircleOutlineIcon fontSize="small" />
                File selected
              </>
            ) : (
              "* Upload a .zip file containing your Excel or CSV file"
            )}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            onClick={() => handleDownloadColumnNames("csv")}
            disabled={isDownloadingColumns}
            sx={{ fontSize: "0.75rem", textTransform: "none", mr: 1 }}
          >
            Download Columns (CSV)
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleDownloadColumnNames("excel")}
            disabled={isDownloadingColumns}
            sx={{ fontSize: "0.75rem", textTransform: "none" }}
          >
            Download Columns (Excel)
          </Button>
        </Grid>
      </Grid>

      {/* Show Excel Fields & DB Fields */}
      <Grid container spacing={2} padding={2}>
        {/* File Fields */}
        <Grid item xs={6}>
          <Typography variant="h6">File Fields</Typography>
          {excelHeaders.map((col, i) => {
            const dbFieldAtIndex = dbFields[i];
            const isMatch = dbFieldAtIndex === col;
            return (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  width: "fit-content",
                  minWidth: 360,
                  p: 1,
                  my: 1,
                  backgroundColor: isMatch
                    ? "rgba(186, 230, 253, 0.35)"
                    : "rgba(239, 154, 154, 0.35)",
                  borderLeft: isMatch
                    ? "4px solid #1976d2"
                    : "4px solid #e53935",
                  backdropFilter: "blur(4px)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  },
                }}
              >
                {col}
              </Paper>
            );
          })}
        </Grid>

        {/* DB Fields */}
        <Grid item xs={6}>
          <Typography variant="h6">DB Columns</Typography>
          {dbFields.map((col, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 1,
                my: 1,
                width: "fit-content",
                minWidth: 360,
                backgroundColor: "rgba(255, 236, 153, 0.35)",
                borderLeft: "4px solid #ffa000",
                backdropFilter: "blur(4px)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                },
              }}
            >
              {col}
            </Paper>
          ))}
        </Grid>
      </Grid>

      {/* Options */}
      <FormControl sx={{ mt: 6 }}>
        <RadioGroup value={selectedOption} onChange={handleOptionChange} row>
          <FormControlLabel
            value="1"
            control={<Radio />}
            label="Existing Mobile Update."
          />
          <FormControlLabel
            value="2"
            control={<Radio />}
            label="Overwrite Existing Data."
          />
          <FormControlLabel
            value="3"
            control={<Radio />}
            label="Add New Numbers."
          />
        </RadioGroup>
      </FormControl>

      {/* Conditional Data Usage Message */}
      {uploadStatus && (
        <Box
          sx={{
            mt: 1,
            mx: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            textAlign: "right",
          }}
        >
          <Typography
            variant="subtitle2"
            color={uploadStatus.allowed ? "success.main" : "error"}
            fontWeight="bold"
          >
            {uploadStatus.message}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" display="block">
              Database: {(uploadStatus.databaseSizeMB / 1024).toFixed(4)} GB
            </Typography>

            <Typography variant="caption" display="block">
              Search Files: {(uploadStatus.folderSizeMB / 1024).toFixed(4)} GB
            </Typography>

            <Typography variant="caption" display="block">
              Total Used: {(uploadStatus.totalUsedMB / 1024).toFixed(4)} GB
            </Typography>

            <Typography variant="caption" display="block">
              Limit: {(uploadStatus.uploadLimitMB / 1024).toFixed(2)} GB
            </Typography>
          </Box>
        </Box>
      )}

      {/* Submit */}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#007bff", color: "white" }}
          onClick={handleSubmit}
          disabled={!allMatch || (uploadStatus && !uploadStatus.allowed)}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
}